import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Alert from '@welldev-group/rcl-alert';
import withQueryParamsProvider from '../../../shared/components/withQueryParamsProvider';
import routes from '../../../shared/routes';
import httpClient from '../../../shared/libraries/httpClient';
import PricingTable from '../../../shared/components/PricingTable/index';
import SubscriptionModeSwitch from './SubscriptionModeSwitch';
import PlanCard from './PlanCard';
import subscriptionModeDetector, {
  subscriptionModes,
  isCustomPlan,
} from './helpers/subscriptionModeDetector';
import CancelScheduledDowngradeModule from './Subcription/CancelScheduledDowngradeModule';

const DOWNGRADE_INDICATOR_KEY = 'downgrade';
const UPGRADE_INDICATOR_KEY = 'upgrade';

const Plans = ({
  affiliateId,
  affiliateDeactivated,
  currentPlanId,
  isSuperAdmin,
  scheduledDowngrade,
  urls,
  downgradedInCurrentMonth,
  lastPlan,
}) => {
  const [plans, setPlans] = useState([]);
  const [monthlyPlans, setMonthlyPlans] = useState([]);
  const [yearlyPlans, setYearlyPlans] = useState([]);
  const [freePlans, setFreePlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState({});
  const [showYearlySubscriptionFee, setShowYearlySubscriptionFee] = useState(
    false,
  );
  const currentlyInCustomPlan = isCustomPlan(currentPlan);

  const fetchCurrentPlan = async planId => {
    const { data } = await httpClient.get(
      routes.billing.plans.fetch({ affiliate_id: affiliateId, id: planId }),
    );

    if (data.plan) {
      setCurrentPlan(data.plan);
    }
  };

  const fetchMonthlyPlans = async () => {
    const paidMonthlyPlans = [];
    const plansWithNoCost = [];
    const { data } = await httpClient.get(
      routes.billing.plans.list({
        affiliate_id: affiliateId,
        period: 'monthly',
        public_only: true,
      }),
    );

    // Detect the free plan and save it to a separate state
    // so that we can show it for both monthly and yearly subscriptions
    data.plans.forEach(plan => {
      if (parseFloat(plan.price) > 0) {
        paidMonthlyPlans.push(plan);
      } else {
        plansWithNoCost.push(plan);
      }
    });

    setFreePlans(plansWithNoCost);
    setMonthlyPlans(paidMonthlyPlans);
  };

  const fetchYearlyPlans = async () => {
    const { data } = await httpClient.get(
      routes.billing.plans.list({
        affiliate_id: affiliateId,
        period: 'yearly',
        public_only: true,
      }),
    );
    setYearlyPlans(data.plans);
  };

  useEffect(() => {
    fetchMonthlyPlans();
    fetchYearlyPlans();
    fetchCurrentPlan(currentPlanId);
  }, []);

  useEffect(() => {
    if (currentlyInCustomPlan) {
      setPlans([currentPlan]);
    } else if (showYearlySubscriptionFee) {
      // Show free plan in both monthly and yearly subscriptions
      setPlans([...freePlans, ...yearlyPlans]);
    } else {
      // Show free plan in both monthly and yearly subscriptions
      setPlans([...freePlans, ...monthlyPlans]);
    }
  }, [
    showYearlySubscriptionFee,
    freePlans,
    monthlyPlans,
    yearlyPlans,
    currentlyInCustomPlan,
  ]);

  useEffect(() => {
    if (currentPlan && currentPlan.period === 'yearly') {
      setShowYearlySubscriptionFee(true);
    } else {
      setShowYearlySubscriptionFee(false);
    }
  }, [currentPlan]);

  return (
    <div className="change-plans container-full">
      <div className="grid grid-gap-20">
        <h1 className="col-12 change-plans__page-title">
          {I18n.t('components.pricing.plans.title')}
        </h1>
        {downgradedInCurrentMonth ? (
          <div className="col-12">
            <Alert
              className="change-plans__plan-unvailability-alert"
              isVisible={downgradedInCurrentMonth}
              type="warning-light"
              hideClose={true}
            >
              {I18n.t(
                'components.pricing.plans.plan_unavailability_alert_text',
                {
                  planName: I18n.t(lastPlan.name, {
                    scope: 'components.plans',
                  }),
                  planPeriod: lastPlan.period,
                },
              )}
            </Alert>
          </div>
        ) : null}
        {scheduledDowngrade ? (
          <div className="col-12">
            <CancelScheduledDowngradeModule
              affiliateId={affiliateId}
              scheduledDowngrade={scheduledDowngrade}
              redirectTo={urls.plans}
            />
          </div>
        ) : null}
        {!currentlyInCustomPlan ? (
          <div className="col-12 change-plans__select-subscription-mode">
            <SubscriptionModeSwitch
              flippedHorizontaly={false}
              subscribeYearly={showYearlySubscriptionFee}
              onModeChange={() =>
                setShowYearlySubscriptionFee(prevState => !prevState)
              }
            />
          </div>
        ) : null}
        <div className="col-12 change-plans__packages">
          <div className="grid grid-gap-20">
            {plans.map(plan => {
              const subscriptionMode = subscriptionModeDetector({
                oldPlan: {
                  name: currentPlan.name,
                  period: currentPlan.period,
                },
                newPlan: {
                  name: plan.name,
                  period: plan.period,
                },
              });
              const isDowngrading =
                subscriptionMode === subscriptionModes.downgrade;
              const canChangePlan = downgradedInCurrentMonth
                ? lastPlan.id === plan.id
                : subscriptionMode !== subscriptionModes.notPossible;

              return (
                <div
                  className="col-12 col-md-6 col-lg-4 col-xlg-3 change-plans__packages-item"
                  key={plan.id}
                >
                  <PlanCard
                    currentPlan={currentPlan}
                    id={plan.id}
                    name={plan.name}
                    price={plan.price}
                    currency={plan.currencyCode}
                    period={plan.period}
                    identifier={plan.identifier}
                    searches={plan.searches}
                    bookings={plan.bookings}
                    travelers={plan.travelers}
                    supportTicket={plan.supportTicket}
                    phone={plan.phone}
                    channels={plan.channels}
                    workspaces={plan.workspaces}
                    domain={plan.domain}
                    affiliateDeactivated={affiliateDeactivated}
                    isDowngrading={isDowngrading}
                    canChangePlan={canChangePlan}
                    planDetailPageUrl={routes.billing.subscriptions[
                      isDowngrading
                        ? DOWNGRADE_INDICATOR_KEY
                        : UPGRADE_INDICATOR_KEY
                    ].detail({
                      affiliate_id: affiliateId,
                      identifier: plan.identifier,
                      period: plan.period,
                    })}
                    disablePlanChange={Boolean(scheduledDowngrade)}
                  />
                </div>
              );
            })}
          </div>
        </div>
        <div className="col-12">
          <PricingTable isSuperAdmin={isSuperAdmin} />
        </div>
      </div>
    </div>
  );
};

Plans.defaultProps = {
  affiliateDeactivated: false,
  isSuperAdmin: false,
  downgradedInCurrentMonth: false,
  lastPlan: null,
  scheduledDowngrade: null,
  urls: {
    billing: '',
    plans: '',
  },
};

Plans.propTypes = {
  currentPlanId: PropTypes.number.isRequired,
  affiliateId: PropTypes.number.isRequired,
  affiliateDeactivated: PropTypes.bool,
  isSuperAdmin: PropTypes.bool,
  downgradedInCurrentMonth: PropTypes.bool,
  lastPlan: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    period: PropTypes.string,
  }),
  scheduledDowngrade: PropTypes.shape({
    id: PropTypes.number,
    plan: PropTypes.shape({}),
    downgradedFeatures: {
      lessWorkspaces: PropTypes.number,
      lessChannels: PropTypes.number,
      customDomainNotSupported: PropTypes.bool,
      lessPhoneSupport: PropTypes.bool,
    },
    downgradeAt: PropTypes.string,
  }),
  urls: PropTypes.shape({
    billing: PropTypes.string,
    plans: PropTypes.string,
  }),
};

export default withQueryParamsProvider(Plans);
